import React from "react";
import { Outlet, useParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useFeatureFlag } from "contexts/ConfigurationProvider/ConfigurationProvider";
import { FeatureFlags } from "contexts/ConfigurationProvider/FeatureFlags";
import useAsyncRequest from "hooks/useAsyncRequest";
import { LoadablePage } from "shared/LoadablePage";
import { ProductPlatform, type Subscription } from "client/api-v2";
import CopyToClipboardWrapper from "components/CopyToClipboardWrapper";
import { EntityProvider } from "components/EntityProvider/EntityProvider";
import NavigationSidebarLayout, { Navigation } from "components/NavigationSidebarLayout/NavigationSidebarLayout";
import { PaperLayout } from "components/PaperLayout/PaperLayout";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { getSubscriptionById } from "../services/subscriptionService";
import { subscriptionRoutes } from "../subscription-routes";

function getValueOrFallbackFor(subscriptionIdValue?: string) {
  return subscriptionIdValue || "UNKNOWN_SUBSCRIPTION";
}

function getPreNavbarFor(subscription?: Subscription) {
  return (
    <CopyToClipboardWrapper value={subscription?.id}>
      <Tooltip title="Subscription ID">
        <span style={{ fontSize: "0.875rem" }}>{subscription?.id}</span>
      </Tooltip>
    </CopyToClipboardWrapper>
  );
}

function getNavLinksFor(subscription?: Subscription, adoptionLedPricingEnabled?: boolean) {
  const subscriptionId = getValueOrFallbackFor(subscription?.id || undefined);

  const navLinks = [
    Navigation.navItem(
      "Details",
      subscriptionRoutes.detail(subscriptionId).root,
      true,
      RbacPolicies.Subscriptions.Read
    ),
  ];

  // Always show History for now
  navLinks.push(
    Navigation.navItem(
      "History",
      subscriptionRoutes.detail(subscriptionId).history,
      false,
      RbacPolicies.Subscriptions.Read
    )
  );

  if (subscription?.plan?.productPlatform === ProductPlatform.OctopusServer) {
    navLinks.push(
      Navigation.navItem(
        "License Emails",
        subscriptionRoutes.detail(subscriptionId).licenseEmails,
        false,
        RbacPolicies.ServerLicenses.Read
      )
    );
  }

  if (subscription?.deactivationHistory && subscription.deactivationHistory.length > 0) {
    navLinks.push(
      Navigation.navItem(
        "Deactivation History",
        subscriptionRoutes.detail(subscriptionId).deactivationHistory,
        false,
        RbacPolicies.Subscriptions.Read
      )
    );
  }

  // Add User Access tab when applicable
  // TODO: Add conditional logic based on subscription status when needed
  navLinks.push(
    Navigation.navItem("User Access", subscriptionRoutes.detail(subscriptionId).userAccess, false, [
      RbacPolicies.Users.Read,
      RbacPolicies.Subscriptions.Read,
    ])
  );

  if (subscription?.plan?.productPlatform === ProductPlatform.OctopusCloud) {
    navLinks.push(
      Navigation.navItem(
        "IP Address Allow List",
        subscriptionRoutes.detail(subscriptionId).ipAddressAllowList,
        false,
        RbacPolicies.CloudSubscriptions.Read
      )
    );
  }

  if (adoptionLedPricingEnabled) {
    navLinks.push(
      Navigation.navItem(
        "Rate Card",
        subscriptionRoutes.detail(subscriptionId).rateCard,
        false,
        RbacPolicies.RateCards.Read
      )
    );
  }

  return navLinks;
}

export function SubscriptionDetailLayout() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const adoptionLedPricingEnabled = useFeatureFlag(FeatureFlags.AdoptionLedPricing);

  const {
    data: subscription,
    loading,
    error,
    refresh,
  } = useAsyncRequest(getSubscriptionById, getValueOrFallbackFor(subscriptionId));

  return (
    <LoadablePage loading={loading && !subscription} error={error}>
      <EntityProvider entity={subscription} refresh={refresh}>
        <NavigationSidebarLayout
          navLinks={getNavLinksFor(subscription, adoptionLedPricingEnabled)}
          name={"Subscription"}
          preNavbarComponent={getPreNavbarFor(subscription)}
        >
          <PaperLayout>
            <Outlet />
          </PaperLayout>
        </NavigationSidebarLayout>
      </EntityProvider>
    </LoadablePage>
  );
}
